import React, { useEffect, useRef, useState } from 'react';
import './HowItWorks.css';

const DURS = [2600, 2400, 2400, 2600];
const PCTS = [0, 34, 67, 100];

const STEPS = [
  {
    tag: 'step one',
    label: 'Audit',
    description:
      'We sit down with you and map out exactly how your business handles inquiries, follow-up, and operations.',
    panel: [
      { dot: 'g', label: 'Intake process', value: 'mapping', valueTone: 'g' },
      { dot: 'g', label: 'Follow-up gaps', value: 'mapping', valueTone: 'g' },
      { dot: 'g', label: 'Tool connections', value: 'mapping', valueTone: 'g' },
    ],
  },
  {
    tag: 'step two',
    label: 'Diagnose',
    description: 'Three gaps. Real dollar values. Based on your actual numbers — not estimates.',
    panel: [
      { dot: 'red', label: 'Missed calls', value: '−$3,800/mo', valueTone: 'red' },
      { dot: 'red', label: 'Lapsed clients', value: '−$2,100/mo', valueTone: 'red' },
      { dot: 'red', label: 'No-shows', value: '−$1,400/mo', valueTone: 'red' },
    ],
  },
  {
    tag: 'step three',
    label: 'Prescribe',
    description:
      'One clear recommendation. What to fix first, how we fix it, and what it costs — before anything starts.',
    panel: [
      { dot: 'green', label: 'Fix recommended', value: 'confirmed', valueTone: 'green' },
      { dot: 'g', label: 'Build time', value: '3 days', valueTone: 'g' },
      { dot: 'g', label: 'Investment', value: '$4,500–$6,000', valueTone: 'g' },
    ],
  },
  {
    tag: 'step four',
    label: 'Deliver',
    description: 'Built, tested, and handed off. Runs inside your existing systems. Most clients are live within two weeks.',
    panel: [
      { dot: 'green', label: 'Built + tested', value: 'done', valueTone: 'green' },
      { dot: 'green', label: 'Team walkthrough', value: 'done', valueTone: 'green' },
      { dot: 'green', label: 'Live in your systems', value: '7–14 days', valueTone: 'green' },
    ],
  },
];

function CheckIcon() {
  return (
    <svg className="hiw-proc-check" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M2 7L5.5 10.5L12 4" />
    </svg>
  );
}

const HowItWorks = () => {
  const [stepPhase, setStepPhase] = useState(() => ['idle', 'idle', 'idle', 'idle']);
  const [fillPct, setFillPct] = useState(0);
  const [fillTransition, setFillTransition] = useState(true);
  const sectionRef = useRef(null);
  const timersRef = useRef([]);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    const clearTimers = () => {
      timersRef.current.forEach((id) => clearTimeout(id));
      timersRef.current = [];
    };

    const schedule = (fn, ms) => {
      const id = setTimeout(fn, ms);
      timersRef.current.push(id);
    };

    const runOnce = () => {
      setStepPhase(['idle', 'idle', 'idle', 'idle']);
      setFillTransition(false);
      setFillPct(0);
      schedule(() => setFillTransition(true), 50);

      let cursor = 300;
      DURS.forEach((dur, i) => {
        schedule(() => {
          setStepPhase((prev) => {
            const next = [...prev];
            next[i] = 'active';
            return next;
          });
        }, cursor);

        schedule(() => {
          setStepPhase((prev) => {
            const next = [...prev];
            next[i] = 'done';
            return next;
          });
          if (i < 3) setFillPct(PCTS[i + 1]);
        }, cursor + dur);

        cursor += dur + 300;
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || hasStartedRef.current) return;
        hasStartedRef.current = true;
        runOnce();
        observer.disconnect();
      },
      { threshold: 0.35 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => {
      observer.disconnect();
      clearTimers();
    };
  }, []);

  return (
    <section ref={sectionRef} id="how-it-works" className="how-it-works section">
      <div className="container hiw">
        <div className="hiw-hdr">
          <p className="hiw-eye">How It Works</p>
          <h2 className="hiw-title">Audit. Diagnose. Prescribe. Deliver.</h2>
          <p className="hiw-sub">Four steps. We find the problem, put a number on it, and ship the fix.</p>
        </div>

        <div className="hiw-proc">
          <div className="hiw-proc-track" aria-hidden="true">
            <div
              className="hiw-proc-fill"
              style={{
                width: `${fillPct}%`,
                transition: fillTransition ? 'width 0.7s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
              }}
            />
          </div>

          {STEPS.map((step, i) => {
            const phase = stepPhase[i];
            const stepClass =
              phase === 'active' ? 'hiw-proc-step active' : phase === 'done' ? 'hiw-proc-step done' : 'hiw-proc-step';

            return (
              <div key={step.label} className={stepClass}>
                <div className="hiw-proc-node">
                  <span className="hiw-proc-num">{String(i + 1).padStart(2, '0')}</span>
                  <CheckIcon />
                </div>
                <div className="hiw-proc-content">
                  <div className="hiw-proc-tag">
                    <span className="hiw-proc-tag-dot" />
                    {step.tag}
                  </div>
                  <div className="hiw-proc-label">{step.label}</div>
                  <p className="hiw-proc-desc">{step.description}</p>
                  <div className="hiw-proc-panel">
                    <div className="hiw-panel-inner">
                      {step.panel.map((row) => (
                        <div key={row.label} className="hiw-panel-row">
                          <div className={`hiw-p-dot ${row.dot}`} />
                          <div className="hiw-p-lbl">{row.label}</div>
                          <div className={`hiw-p-val ${row.valueTone}`}>{row.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
